import { Injectable, inject } from '@angular/core';
import { Observable, from } from 'rxjs';
import { map } from 'rxjs/operators';
import { Api } from '../../../api/gse/api';
import {
  getProcurements,
  GetProcurements$Params,
  getProcurementDetails,
  createRequisition,
  CreateRequisition$Params,
  createReplenishment,
  CreateReplenishment$Params,
} from '../../../api/gse/functions';
import { GetProcurementsResponse } from '../../../api/gse/models/get-procurements-response';
import { ProcurementResponse } from '../../../api/gse/models/procurement-response';
import { CreateRequisitionRequest } from '../../../api/gse/models/create-requisition-request';
import { CreateReplenishmentRequest } from '../../../api/gse/models/create-replenishment-request';
import { ProcurementListItemDto } from '../../../api/gse/models/procurement-list-item-dto';
import { ProcurementDetailedDto } from '../../../api/gse/models/procurement-detailed-dto';
import { ProcurementStatus } from '../../../api/gse/models/procurement-status';
import { StrictHttpResponse } from '../../../api/gse/strict-http-response';
import { RequisitionDto } from '../../../api/gse/models/requisition-dto';

type ProcurementItemWithRequisitions = ProcurementListItemDto & {
  requisitions?: Array<RequisitionDto>;
};

export interface ProcurementTableItem {
  id?: number;
  requestId?: string;
  spareParts: string;
  createdDate: string;
  reqDate?: string;
  qtyRequested?: number;
  qtyPurchased?: number;
  status?: ProcurementStatus;
  vendor: string;
}

@Injectable({
  providedIn: 'root',
})
export class ProcurementService {
  private api = inject(Api);

  getProcurements(params: GetProcurements$Params): Observable<GetProcurementsResponse> {
    return from(this.api.invoke(getProcurements, params));
  }

  getProcurementsResponse(
    params: GetProcurements$Params
  ): Observable<StrictHttpResponse<GetProcurementsResponse>> {
    return from(this.api.invoke$Response(getProcurements, params));
  }

  /**
   * GET /procurements/{id}. Normalizes wrapped `{ data }` (array or object) or an unwrapped detailed DTO body.
   */
  getProcurementDetails(id: number): Observable<ProcurementDetailedDto | null> {
    return from(this.api.invoke(getProcurementDetails, { id })).pipe(
      map((body) => this.normalizeProcurementDetailsBody(body))
    );
  }

  private normalizeProcurementDetailsBody(body: unknown): ProcurementDetailedDto | null {
    if (body == null || typeof body !== 'object') return null;
    const o = body as Record<string, unknown>;

    if ('data' in o) {
      const d = o['data'];
      if (d == null) return null;
      if (Array.isArray(d)) {
        const first = d[0];
        return first != null && typeof first === 'object'
          ? (first as ProcurementDetailedDto)
          : null;
      }
      return d as ProcurementDetailedDto;
    }

    if (
      Array.isArray(o['requisitions']) ||
      typeof o['id'] === 'number' ||
      typeof o['requestId'] === 'string'
    ) {
      return body as ProcurementDetailedDto;
    }

    return null;
  }

  createRequisition(
    body: CreateRequisitionRequest
  ): Observable<StrictHttpResponse<ProcurementResponse>> {
    const params: CreateRequisition$Params = { body };
    return from(this.api.invoke$Response(createRequisition, params));
  }

  createReplenishment(
    id: number,
    body: CreateReplenishmentRequest
  ): Observable<StrictHttpResponse<ProcurementResponse>> {
    const params: CreateReplenishment$Params = { id, body };
    return from(this.api.invoke$Response(createReplenishment, params));
  }

  mapProcurementToTable(p: ProcurementListItemDto): ProcurementTableItem {
    // List DTO is summary-only; line-level aggregates load with procurement details.
    return {
      id: p.id,
      requestId: p.requestId,
      spareParts: '--',
      createdDate: p.createdAt ? this.formatDate(p.createdAt) : '--',
      qtyRequested: 0,
      qtyPurchased: 0,
      status: p.status,
      vendor: '--',
    };
  }

  formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    return `${date.getDate()}-${months[date.getMonth()]}-${date.getFullYear().toString().slice(-2)}`;
  }
}
